import React, { useMemo } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Extrapolation,
  interpolate,
  interpolateColor,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import OnboardingCard, {
  OnboardingCardProps,
} from 'shared/components/vxl/cards/OnboardingCard/OnboardingCard';
import WalletOnboardingCard, {
  WalletOnboardingCardProps,
} from 'shared/components/vxl/cards/OnboardingCard/WalletOnboardingCard/WalletOnboardingCard';

// Break point for if we consider it a full swipe or not.  if swiped more than 120 pts the next card will be rotated in
const SWIPE_WIDTH_THRESHOLD = 120;
const SWIPE_VELOCITY_THRESHOLD = 1000;
export interface OnboardingCarouselProps {
  //the cards to be displayed in the stack
  onboardingCards: (OnboardingCardProps | WalletOnboardingCardProps)[];
}

interface AnimatableOnboardingCardProps {
  index: number;
  selectedIndex: SharedValue<number>;
  totalCardsAmount: number;
  translateX: SharedValue<number>;
  card: OnboardingCardProps | WalletOnboardingCardProps;
  topLevelWidth: SharedValue<number>;
}

const AnimatableOnboardingCard = ({
  index,
  selectedIndex,
  totalCardsAmount,
  translateX,
  card,
  topLevelWidth,
}: AnimatableOnboardingCardProps) => {
  //generates the tint over the cards below the selected card.
  const useGenerateOverlayStyle = () => {
    return useAnimatedStyle(() => {
      /*Adjusted index is the index of the card in relation to the current selected card
        ex: onboardingCards = [card0, card1, card2]
        selectedIndex = 1
        index = 2 
        this would return an adjusted index of 1 as it is the 1st card after the current selected card
       */
      const adjustedIndex =
        (index - selectedIndex.value + totalCardsAmount) % totalCardsAmount;

      const tintValue = adjustedIndex * 0.08;
      const backgroundColor = interpolateColor(
        translateX.value,
        [-SWIPE_WIDTH_THRESHOLD, 0, SWIPE_WIDTH_THRESHOLD],
        [
          `rgba(0,0,0,${tintValue - 0.08})`,
          `rgba(0,0,0,${tintValue})`,
          `rgba(0,0,0,${tintValue - 0.08})`,
        ],
      );
      return {
        backgroundColor,
        display: adjustedIndex === 0 ? 'none' : 'flex',
      };
    });
  };

  const useGenerateStyle = () => {
    /*Note: all animation values must be done within the closure of the useAnimatedStyles or they will not update*/
    return useAnimatedStyle(() => {
      const adjustedIndex =
        (index - selectedIndex.value + totalCardsAmount) % totalCardsAmount;

      const baseLineScale = topLevelWidth.value - 16 * adjustedIndex;
      const baseLineYPos = Math.min(adjustedIndex, 2) * 8;

      // a interpolation is used her to give the cards a slide and shrink affect as the top card is slid
      const scale = interpolate(
        translateX.value,
        [-SWIPE_WIDTH_THRESHOLD, 0, SWIPE_WIDTH_THRESHOLD],
        [baseLineScale + 16, baseLineScale, baseLineScale + 16],
        Extrapolation.CLAMP,
      );
      const yTranslationPosition = interpolate(
        translateX.value,
        [-SWIPE_WIDTH_THRESHOLD, 0, SWIPE_WIDTH_THRESHOLD],
        [baseLineYPos - 8, baseLineYPos, baseLineYPos - 8],
        Extrapolation.CLAMP,
      );
      const isFirstCard = adjustedIndex === 0;
      const zIndex = Math.abs(adjustedIndex - totalCardsAmount);
      if (isFirstCard) {
        return {
          zIndex: zIndex,
          width: '100%',
          transform: [{ translateX: translateX.value }],
        };
      }
      return {
        zIndex: zIndex,
        width: scale,
        transform: [{ translateY: yTranslationPosition }],
      };
    });
  };
  const animatedStyle = useGenerateStyle();
  const backgroundStyle = useGenerateOverlayStyle();
  if ('actionText' in card) {
    const onboardingCardProps = card as OnboardingCardProps;
    return (
      <Animated.View
        key={card.bodyText}
        style={[animatedStyle, baseStyles.card]}
      >
        <OnboardingCard {...onboardingCardProps} />
        <Animated.View style={[baseStyles.tintOverlay, backgroundStyle]} />
      </Animated.View>
    );
  }
  const onboardingCardProps = card as WalletOnboardingCardProps;
  return (
    <Animated.View key={card.bodyText} style={[animatedStyle, baseStyles.card]}>
      <WalletOnboardingCard {...onboardingCardProps} />
      <Animated.View style={[baseStyles.tintOverlay, backgroundStyle]} />
    </Animated.View>
  );
};

export const OnboardingCarousel = ({
  onboardingCards,
}: OnboardingCarouselProps) => {
  // Gesture Handler requires a stable gesture instance.
  // So here we are telling React compiler not to run useMemo optimizations on this component
  'use no memo';

  //the dx position of the top card in the stack
  const translateX = useSharedValue(0);
  //the current top card in the stack
  const selectedIndex = useSharedValue(0);
  //Used to slide the card offscreen.  this is to hide the z index swap and give the illusion of a rotating stack
  const screenWidth = useSharedValue(0);
  const containerWidth = useSharedValue(0);

  const gesture = useMemo(
    () =>
      Gesture.Pan()
        .onChange((event) => {
          translateX.value = event.translationX;
        })
        .onFinalize((event) => {
          if (
            Math.abs(event.translationX) > SWIPE_WIDTH_THRESHOLD ||
            Math.abs(event.velocityX) > SWIPE_VELOCITY_THRESHOLD
          ) {
            translateX.value = withSpring(
              event.translationX > 0 ? screenWidth.value : -screenWidth.value,
              { mass: 0.7 },
              () => {
                //update the selected index using modulo operator to reset us back to 0 once we rotate through the entire stack
                selectedIndex.value =
                  (selectedIndex.value + 1) % onboardingCards.length;
                //don't use withSpring here.  using a animation on your shared value within a animation closure like this causes weird hitches.
                translateX.value = 0;
              },
            );
          } else {
            //If swipe was'nt big enough to make it past the break even point then reset x position of top card
            translateX.value = withSpring(0);
          }
        }),
    [onboardingCards.length, screenWidth.value, selectedIndex, translateX],
  );

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        style={baseStyles.container}
        onLayout={() => (screenWidth.value = Dimensions.get('window').width)}
      >
        <View
          style={baseStyles.staticContainer}
          onLayout={(event) =>
            (containerWidth.value = event.nativeEvent.layout.width)
          }
        >
          {onboardingCards.map((card, index) => {
            return (
              <AnimatableOnboardingCard
                key={card.bodyText}
                index={index}
                selectedIndex={selectedIndex}
                totalCardsAmount={onboardingCards.length}
                translateX={translateX}
                card={card}
                topLevelWidth={containerWidth}
              />
            );
          })}
        </View>
      </Animated.View>
    </GestureDetector>
  );
};

const baseStyles = StyleSheet.create({
  card: {
    position: 'absolute',
    width: '100%',
  },
  container: {
    width: '100%',
  },
  staticContainer: {
    alignItems: 'center',
    minHeight: 146,
    position: 'relative',
    width: '100%',
  },
  tintOverlay: {
    ...StyleSheet.absoluteFill,
    zIndex: 100,
  },
});
